import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Card, IconButton, Modal, Portal, Switch, Text } from 'react-native-paper';
import { colors, spacing, typography } from '../../../constants/theme';
import { QuestionField, ReviewItem } from '../AdminDashboard.types';

interface AdminQuestionModalProps {
  visible: boolean;
  item: ReviewItem | null;
  localQuestionOrder: QuestionField[];
  hasQuestionOrderChanges: boolean;
  onDismiss: () => void;
  onAddQuestion: () => void;
  onDeactivateQuestion: (itemId: number, fieldId: string) => void;
  onRestoreQuestion: (itemId: number, fieldId: string) => void;
  onToggleMultiplicity: (itemId: number, fieldId: string, value: boolean) => void;
  onReorderQuestion: (fieldId: string, direction: 'up' | 'down') => void;
  onSaveQuestionOrder: () => void;
}

const AdminQuestionModal: React.FC<AdminQuestionModalProps> = ({
  visible,
  item,
  localQuestionOrder,
  hasQuestionOrderChanges,
  onDismiss,
  onAddQuestion,
  onDeactivateQuestion,
  onRestoreQuestion,
  onToggleMultiplicity,
  onReorderQuestion,
  onSaveQuestionOrder,
}) => {
  const schemaFields = Array.isArray(item?.questionsSchema?.fields) ? item.questionsSchema.fields : [];
  const questionsToDisplay = visible && localQuestionOrder.length > 0
    ? localQuestionOrder
    : schemaFields;
  const activeQuestions = questionsToDisplay.filter((field) => !field.deprecated);

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modal}
      >
        <ScrollView>
          <Text style={styles.modalTitle}>
            Questions for: {item?.name}
          </Text>

          {questionsToDisplay.map((field) => {
            const activeIndex = activeQuestions.findIndex((question) => question.id === field.id);
            const isFirstActive = activeIndex === 0;
            const isLastActive = activeIndex === activeQuestions.length - 1;

            return (
              <Card key={field.id} style={[styles.questionCard, field.deprecated && styles.deactivatedCard]}>
                <Card.Content>
                  <View style={styles.questionHeader}>
                    <View style={styles.questionInfo}>
                      <Text style={styles.questionLabel}>{field.label}</Text>
                      <Text style={styles.questionType}>Type: {field.type}</Text>
                      {(field.type === 'number' || field.type === 'plusminus') && !field.deprecated && (
                        <View style={styles.multiplicityToggle}>
                          <Text style={styles.multiplicityLabel}>Triggers multiplicity:</Text>
                          <Switch
                            value={field.triggersMultiplicity === true}
                            onValueChange={(value) => {
                              if (item) {
                                onToggleMultiplicity(item.id, field.id, value);
                              }
                            }}
                          />
                        </View>
                      )}
                      {field.deprecated && (
                        <Text style={styles.deactivatedText}>
                          Deactivated: {new Date(field.deprecatedAt as string).toLocaleDateString()}
                        </Text>
                      )}
                    </View>
                    <View style={styles.questionActions}>
                      {!field.deprecated && (
                        <View style={styles.reorderButtons}>
                          <IconButton
                            icon="chevron-up"
                            size={20}
                            onPress={() => onReorderQuestion(field.id, 'up')}
                            style={styles.reorderIconButton}
                            mode="outlined"
                            disabled={isFirstActive}
                          />
                          <IconButton
                            icon="chevron-down"
                            size={20}
                            onPress={() => onReorderQuestion(field.id, 'down')}
                            style={styles.reorderIconButton}
                            mode="outlined"
                            disabled={isLastActive}
                          />
                        </View>
                      )}
                      {!field.deprecated ? (
                        <Button
                          mode="outlined"
                          onPress={() => {
                            if (item) {
                              onDeactivateQuestion(item.id, field.id);
                            }
                          }}
                          style={styles.deprecateButton}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          mode="outlined"
                          onPress={() => {
                            if (item) {
                              onRestoreQuestion(item.id, field.id);
                            }
                          }}
                          style={styles.restoreButton}
                        >
                          Restore
                        </Button>
                      )}
                    </View>
                  </View>
                </Card.Content>
              </Card>
            );
          }) || <Text>No questions defined</Text>}

          {hasQuestionOrderChanges && (
            <View style={styles.orderChangesNotice}>
              <Text style={styles.orderChangesText}>Questions have been reordered. Click Save to apply changes.</Text>
              <Button
                mode="contained"
                onPress={onSaveQuestionOrder}
                style={styles.saveOrderButton}
              >
                Save Order
              </Button>
            </View>
          )}

          <View style={styles.questionModalActions}>
            <Button
              mode="outlined"
              onPress={onAddQuestion}
              style={styles.addQuestionButton}
            >
              + Add New Question
            </Button>
            <Button
              mode="contained"
              onPress={onDismiss}
              style={styles.closeButton}
            >
              Close
            </Button>
          </View>
        </ScrollView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '90%',
    minHeight: '70%',
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
  },
  questionCard: {
    marginBottom: spacing.md,
  },
  deactivatedCard: {
    backgroundColor: colors.surface,
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
  },
  questionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  questionInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  questionActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'center',
  },
  questionLabel: {
    ...typography.body1,
    fontWeight: '600' as const,
    marginBottom: spacing.xs,
  },
  questionType: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  multiplicityToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.xs,
    gap: spacing.sm,
  },
  multiplicityLabel: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  deactivatedText: {
    ...typography.caption,
    color: colors.error,
    marginTop: spacing.xs,
  },
  reorderButtons: {
    flexDirection: 'column',
    gap: spacing.xs,
    marginRight: spacing.sm,
  },
  reorderIconButton: {
    margin: 0,
    borderWidth: 1,
    borderColor: colors.gray300,
  },
  deprecateButton: {
    borderColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
  },
  restoreButton: {
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  orderChangesNotice: {
    backgroundColor: colors.primary + '10',
    padding: spacing.md,
    borderRadius: 8,
    marginTop: spacing.md,
    alignItems: 'center',
  },
  orderChangesText: {
    ...typography.body,
    color: colors.primary,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  saveOrderButton: {
    backgroundColor: colors.primary,
  },
  questionModalActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  addQuestionButton: {
    borderColor: colors.primary,
    flex: 1,
  },
  closeButton: {
    marginTop: spacing.lg,
  },
});

export default AdminQuestionModal;
